/**
 * Soft≠Live Page162 — SA Caps residual liquid (page80/125 buses + status).
 * Mount: /sa/caps
 * Buses:
 *   GET /sa/caps/status (always-on Live posture)
 *   GET /sa/catalog (capabilities · holders when allowed)
 * Soft residual deleted:
 *  - sandpit grid without liquid rail / KPI / Re-read / status SoT
 *  - bare "Refresh catalogue" / Soft invent held bits when GET fails
 *  - Soft capability-bit revoke CTA invent (no revoke route — remains absent)
 */
import { useMemo } from "react";
import { html, F, Ico, Card, Alert, Ticker, go } from "./ui.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";
import * as D from "./data.js";
import { Unavailable } from "./sa-shared.js";

const CAP_COPY = Object.fromEntries(D.SA_CAPS.map((c) => [c.id, c]));

/** Pure: flatten Live capability rows — never invent Soft held flags. */
export function capsRowsOf(pack) {
  const caps = (pack && pack.capabilities) || [];
  return Array.isArray(caps) ? caps : [];
}

/** Pure: KPI strip from Catalogue only. */
export function capsKpisOf(rows, known) {
  const list = Array.isArray(rows) ? rows : [];
  const held = list.filter((c) => c && c.held).length;
  const wild = list.filter((c) => c && c.wildcard).length;
  const orphan = list.filter((c) => c && Number(c.granted_to) === 0 && !c.held).length;
  return {
    total: list.length,
    held,
    wild,
    orphan: known ? orphan : 0,
    known: !!known,
  };
}

/** Pure: seat guess when status not yet loaded. */
export function canReadCapsOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin") return true;
  if (r !== "sa_staff") return false;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) =>
    /sa_audit_read|sa_full_access|sa_full/i.test(String(c || "").trim()),
  );
}

export function SaCapsDesk() {
  const { me } = useSession();
  const posture = useApi(path("get__sa_caps_status"));
  // Fail-closed while loading — Soft invent catalog seat deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const seat = post.catalog_allowed === true;
  const detail = post.detail || null;
  const cat = useApi(seat ? path("get__sa_catalog") : "");
  const caps = useMemo(() => capsRowsOf(cat.data), [cat.data]);
  const known = !!(cat.data && cat.data.capability_holders_known);
  const kpis = useMemo(() => capsKpisOf(caps, known), [caps, known]);
  const denied = !!(cat.error && /\b40[13]\b|forbidden|insufficient|capability/i.test(String(cat.error)));

  const reRead = () => {
    posture.reload && posture.reload();
    cat.reload && cat.reload();
  };

  if (!seat) {
    return html`<div className="sa-caps-desk sa-caps-desk--liquid"
      data-cmc-page="page125-sa-caps" data-cmc-legacy="page80-sa-caps"
      data-cmc-residual="page162-caps">
      <div className="sa-caps-desk__rail">
        <div>
          <p className="eyebrow">Platform · Capabilities</p>
          <h2 className="sa-caps-desk__title">Seat closed</h2>
          <p className="note">Capability bits stay empty until the catalogue seat opens.</p>
        </div>
        <div className="btn-row sa-caps-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>
            <${Ico} d="refresh" w=${14}/> Re-read posture
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/team")}>Staff</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/audit")}>Audit</button>
        </div>
      </div>
      <${Card} title="Caps posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            Catalogue sealed · write never on this desk
            · ${detail || "not available on this account"}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Capability catalogue not on this account"
        say=${"Capability catalogue needs superadmin or staff audit read. This account is "
          + (me && me.role ? me.role : "operator")
          + " — Capability bits are not invented locally."}/>
      <${Card} title="What you can do" meta="gated">
        <p className="note">No catalogue rows are offered on this grant.</p>
      <//>
    </div>`;
  }

  if (!caps.length && (cat.loading || cat.error || !cat.data)) {
    return html`<div className="sa-caps-desk sa-caps-desk--liquid"
      data-cmc-page="page125-sa-caps" data-cmc-legacy="page80-sa-caps"
      data-cmc-residual="page162-caps">
      <div className="sa-caps-desk__rail">
        <div>
          <p className="eyebrow">Platform · Capabilities</p>
          <h2 className="sa-caps-desk__title">Catalogue readout</h2>
          <p className="note">Catalogue loads only after the seat opens.</p>
        </div>
        <div className="btn-row sa-caps-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${cat.loading || posture.loading}>
            <${Ico} d="refresh" w=${14}/> Re-read
          </button>
          <button type="button" className="btn btn--go btn--sm"
            onClick=${() => go("/sa/team")}>Staff grants</button>
        </div>
      </div>
      <${Card} title="Caps posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            catalog_allowed yes · revoke route ${post.capability_bit_revoke_exists ? "exists" : "absent"}
            · grant write on Staff
          </p>` : null}
      <//>
      ${cat.error ? html`
        <${Alert} tone=${denied ? "warn" : "bad"} title=${denied ? "Catalogue gated" : "Catalogue read failed"}
          say=${String(cat.error)}/>` : null}
      <${Card} title="What you can do" meta="Empty or gated">
        <${Unavailable} r=${cat} what="the capability catalogue"/>
      <//>
    </div>`;
  }

  const orphans = known
    ? caps.filter((c) => Number(c.granted_to) === 0 && !c.held)
    : [];

  return html`<div className="sa-caps-desk sa-caps-desk--liquid"
    data-cmc-page="page125-sa-caps" data-cmc-legacy="page80-sa-caps"
    data-cmc-residual="page162-caps">
    <div className="sa-caps-desk__rail">
      <div>
        <p className="eyebrow">Platform · Capabilities</p>
        <h2 className="sa-caps-desk__title">Default-deny</h2>
        <p className="note">
          Read-out from the live catalogue only when posture allows.
          Soft revoke invent is forbidden — change grants on Staff.
          Posture only.
        </p>
      </div>
      <div className="btn-row sa-caps-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${cat.loading || posture.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/sa/team")}>Change grants on Staff</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/tenant")}>Tenants</button>
      </div>
    </div>

    <${Card} title="Caps posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-caps-desk__kpis" role="group" aria-label="Caps posture">
          ${[
            ["Seat", seat ? "open" : "gated", seat ? "1" : "0", "status Live"],
            ["Write", "none", "0", "read-only surface"],
            ["Grant", "Staff desk", "1", "Staff"],
            ["Revoke bit", "absent", "0", "no Soft CTA"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-caps-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          catalogue follows posture
          · this desk never grants write
        </p>` : null}
    <//>

    <${Alert} tone="info" title="Read-out, not a grant writer"
      say="Grants are written on Staff (provision / TTL). Close a tenant on Tenants. There is no capability-bit revoke route — Success toasts without a real write stay forbidden."/>

    ${cat.error ? html`
      <${Alert} tone=${denied ? "warn" : "bad"} title="Catalogue partial failure"
        say=${String(cat.error)}/>` : null}

    <div className="sa-caps-desk__kpis" role="group" aria-label="Capability KPIs">
      ${[
        ["Defined", kpis.total, kpis.total ? "1" : "0", "Catalogue"],
        ["You hold", kpis.held, kpis.held ? "1" : "0", me && me.role ? me.role : "principal"],
        ["Wildcards", kpis.wild, kpis.wild ? "1" : "0", "full-access flags"],
        ["Nobody holds", kpis.orphan, kpis.orphan ? "1" : "0", known ? "roster Live" : "holders unknown"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-caps-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${Card} ai title="What you can do"
        meta=${kpis.held + " of " + kpis.total + " held"}
        sub="Default-deny. An empty capability set satisfies nothing — there is no accidental wildcard. This page is a live readout from the capability catalogue.">
      <div className="capgrid sa-caps-desk__grid">
        ${caps.map((c) => {
          const copy = CAP_COPY[c.id] || {};
          return html`
          <div key=${c.id} className="cap" data-on=${c.held ? "1" : "0"}
              data-unused=${(copy.unused || (Number(c.granted_to) === 0 && !c.held)) ? "1" : "0"}
              style=${{ textAlign: "left", width: "100%" }}>
            <span className="cap__h">
              <b>${copy.label || c.id}</b>
              ${c.wildcard && html`<span className="chip" data-tone="bad">wildcard</span>`}
              ${copy.unused && html`<span className="chip" data-tone="warn">reserved</span>`}
              <span className="chip" data-tone=${c.held ? "ai" : "off"} style=${{ marginLeft: "auto" }}>
                ${c.held ? "held" : "not held"}</span>
            </span>
            <code>${c.id}</code>
            <span>${copy.say || "No description has been written for this capability yet."}</span>
            <span className="m" style=${{ fontSize: "var(--t-micro)", color: "var(--fg-3)" }}>
              ${!known
                ? "holder count unavailable"
                : c.held
                  ? (Number(c.granted_to) > 1
                      ? `you hold this · ${c.granted_to} on the staff roster`
                      : "you hold this")
                  : (Number(c.granted_to) === 0
                      ? "nobody currently holds this"
                      : `${c.granted_to} staff hold this`)}
            </span>
          </div>`;
        })}
      </div>
    <//>

    ${orphans.length ? html`
      <${Card} title="Granted to nobody" meta="right now roster"
          sub="Defined and enforceable, but no active staff member currently holds it — and you do not hold it either. Counted from the live staff roster.">
        ${orphans.map((c) => html`
          <div key=${c.id} className="verdict" data-ok="0">
            <span className="verdict__st"><${Ico} d="ban" w=${13}/></span>
            <span className="txt"><b>${(CAP_COPY[c.id] || {}).label || c.id}</b>
              <span>${(CAP_COPY[c.id] || {}).say || "No active staff member holds this."}</span></span>
          </div>`)}
      <//>` : null}
  </div>`;
}

export function Caps() {
  return html`<${F} data-cmc-page="page80-sa-caps-mount">
    <${SaCapsDesk}/>
  <//>`;
}
